import { BoxProps } from '@chakra-ui/react'
import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { colors } from '../../../theme/colors'
import { CustomLegend } from '../StackedChart/customChartComponents/CustomLegend'
import { FloatingChartTooltipShell } from '../chartTooltip/ChartTooltipPointerContext'
import { FloatingCartesianChartTooltip } from '../chartTooltip/FloatingCartesianChartTooltip'
import { CategoryAxisTick } from '../common/CategoryAxisTick'
import { RechartsWrapper } from '../common/RechartsWrapper'
import {
  BAR_CORNER_RADIUS,
  CHART_GRID_STROKE,
  CHART_HEIGHT,
  CHART_MIN_WIDTH,
  CHART_X_AXIS_LABEL_ROW_GAP,
  CHART_X_AXIS_LABEL_ROW_HEIGHT,
  HORIZONTAL_BAR_SIZE,
  HORIZONTAL_CHART_LEGEND_MARGIN_TOP,
  HORIZONTAL_CHART_MIN_ROW_COUNT,
  HORIZONTAL_CHART_X_AXIS_HEIGHT,
  HORIZONTAL_CHART_Y_AXIS_LABEL_GAP,
  HORIZONTAL_CHART_Y_AXIS_LABEL_HEIGHT,
} from '../common/chart.constants'
import { ChartData } from '../common/chart.types'
import { formatChartDataLabelValue } from '../common/chartFormat'
import { ChartGrid } from '../common/chartGridLines'
import { getNiceTicks, niceCeil } from '../common/useCustomRecharts'

export type HorizontalChartProps = BoxProps & {
  chartData: ChartData
  /** Value axis label (e.g. "Days"). Shown on the bottom for horizontal layout. */
  valueAxisLabel?: string
  /** Legend label for the single series (e.g. "Average Dwell"). */
  legendLabel?: string
  /** Bar thickness in px (default matches `HORIZONTAL_BAR_SIZE`). */
  barSize?: number
  /**
   * Minimum outer height (px). Rows grow the chart beyond this; the floor keeps the
   * plot from shrinking when a filter reduces the row count. Pass `0` for a purely
   * content-driven height.
   */
  minChartHeight?: number
  /** Hide the built-in legend (render it externally instead). */
  hideLegend?: boolean
  /** When true, show the numeric value at the end of each bar. */
  showBarValueLabels?: boolean
  /** Override bar fill (defaults to theme `chart.barFill`). Port-to-Port unlading dwell. */
  barFillColor?: string
}

export const HorizontalChart = ({
  chartData,
  valueAxisLabel = 'Value',
  legendLabel = 'Value',
  barSize = HORIZONTAL_BAR_SIZE,
  minChartHeight = 0,
  hideLegend = false,
  showBarValueLabels = false,
  barFillColor,
  ...props
}: HorizontalChartProps) => {
  const chartValues = chartData?.chartValues ?? []
  const valueKey = chartData.valueKey ?? 'value'
  const nameKey = chartData.nameKey ?? 'key'
  const values = useMemo(
    () =>
      chartValues.map(p => {
        const rawVal = p[valueKey]
        return typeof rawVal === 'number' ? rawVal : Number(rawVal) || 0
      }),
    [chartValues, valueKey],
  )
  const maxValue = values.length ? Math.max(...values) : 0
  const top = niceCeil(maxValue)
  const xTicks = useMemo(() => getNiceTicks(0, top, 5), [top])

  const itemCount = chartValues.length
  const rowCountForHeight = Math.max(itemCount, HORIZONTAL_CHART_MIN_ROW_COUNT)
  const yAxisRowsHeight =
    rowCountForHeight * HORIZONTAL_CHART_Y_AXIS_LABEL_HEIGHT +
    (rowCountForHeight - 1) * HORIZONTAL_CHART_Y_AXIS_LABEL_GAP
  const xAxisLabelChrome =
    CHART_X_AXIS_LABEL_ROW_HEIGHT + CHART_X_AXIS_LABEL_ROW_GAP
  const contentBasedHeight =
    itemCount === 0
      ? CHART_HEIGHT
      : yAxisRowsHeight + HORIZONTAL_CHART_X_AXIS_HEIGHT + xAxisLabelChrome
  const resolvedOuterHeight = Math.max(minChartHeight, contentBasedHeight)

  const marginTop = hideLegend ? 0 : HORIZONTAL_CHART_LEGEND_MARGIN_TOP
  const barFill = barFillColor ?? colors.chart.barFill

  const barChart = (
    <BarChart
      layout="vertical"
      data={chartValues}
      barCategoryGap={0}
      margin={{
        top: marginTop,
        right: showBarValueLabels ? 44 : 8,
        bottom: 8,
        left: 8,
      }}
    >
      <XAxis
        type="number"
        axisLine={false}
        tickLine={false}
        domain={[0, top]}
        ticks={xTicks}
        tick={{ fill: 'white', fontSize: 10, fontWeight: 500 }}
        height={HORIZONTAL_CHART_X_AXIS_HEIGHT}
        padding={{ left: 8, right: showBarValueLabels ? 44 : 8 }}
      />
      <YAxis
        type="category"
        dataKey={nameKey}
        padding={{ top: 2, bottom: 0 }}
        axisLine={false}
        tickLine={false}
        width={120}
        tick={
          <CategoryAxisTick
            categoryAxisIsDate={chartData.categoryAxisIsDate}
            dateFormat={chartData.categoryAxisDateFormat}
            dateLayout="horizontalBarY"
            unparseableTruncate={{
              maxLength: 16,
              angle: 0,
              textAnchor: 'end',
              fontSize: 10,
              fontWeight: 500,
              dx: -4,
            }}
          />
        }
      />
      <CartesianGrid
        stroke={CHART_GRID_STROKE}
        horizontal={false}
        syncWithTicks
        vertical={ChartGrid.horizontalBarVerticalLine}
      />
      <Tooltip
        shared={false}
        cursor={false}
        content={
          <FloatingCartesianChartTooltip
            floatingLayout="bar"
            nameKey={nameKey}
            series={[
              {
                dataKey: valueKey,
                name: legendLabel,
                color: barFill,
              },
            ]}
            categoryAxisDateFormat={chartData.categoryAxisDateFormat}
            categoryAxisIsDate={chartData.categoryAxisIsDate}
            valueFormat={chartData.valueFormat}
          />
        }
      />
      {!hideLegend && (
        <Legend
          content={<CustomLegend />}
          verticalAlign="top"
          wrapperStyle={{ paddingBottom: 10 }}
        />
      )}
      <Bar
        dataKey={valueKey}
        name={legendLabel}
        fill={barFill}
        radius={[0, BAR_CORNER_RADIUS, BAR_CORNER_RADIUS, 0]}
        barSize={barSize}
        isAnimationActive={false}
      >
        {showBarValueLabels ? (
          <LabelList
            dataKey={valueKey}
            position="right"
            offset={6}
            fill="white"
            fontSize={12}
            fontWeight={700}
            formatter={(v: number) =>
              formatChartDataLabelValue(v, chartData.valueFormat)
            }
          />
        ) : null}
      </Bar>
    </BarChart>
  )

  const wrapperCommon = {
    ...props,
    yAxisLabel: '',
    xAxisLabel: valueAxisLabel,
    chartHeight: resolvedOuterHeight,
    minW: `${CHART_MIN_WIDTH}px`,
  }

  return (
    <FloatingChartTooltipShell>
      {mouseHandlers => (
        <RechartsWrapper
          {...wrapperCommon}
          chartAreaMouseTracking={mouseHandlers}
        >
          {barChart}
        </RechartsWrapper>
      )}
    </FloatingChartTooltipShell>
  )
}
